import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { fetchCached } from "@/lib/publicCache";
import { API } from "@/App";
import { ArrowRight, Check, Baby, Heart, Users, Shield, Star, ExternalLink, Sparkles, CalendarClock, Zap, Dumbbell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { price } from "@/config/currency";
import PositionedImage from "@/components/PositionedImage";
import Picture from "@/components/Picture";
import Seo from "@/components/Seo";
import { usePublicPageLoading, usePublicPageReady } from "@/components/PublicPageReadiness";

const HomePage = () => {
  const [content, setContent] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  usePublicPageLoading(loading);
  const pageReady = usePublicPageReady();

  // Category icon mapping for services
  const categoryIconMap = {
    pediatric: Baby,
    pregnancy: Heart,
    standard: Users
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [homepage, services] = await Promise.all([
          fetchCached("cms:homepage", "/cms/homepage"),
          fetchCached("services", "/services"),
        ]);
        setContent(homepage);
        setServices((services || []).slice(0, 3));
      } catch (error) {
        console.error("Error fetching content:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const defaultContent = {
    services_title: "Our",
    services_title_highlight: "Services",
    services_subtitle: "Specialist physiotherapy services tailored to your child’s individual needs.",
  };

  const c = content || defaultContent;

  return (
    <div className="pt-20">
      <Seo
        title="Child Physio in Sandy, Bedford & Hitchin | Growing Together Physiotherapy"
        description="Specialist child physio for babies, children and young people up to age 25. Paediatric physiotherapy in Sandy, Bedford and Hitchin from a HCPC, CSP and APCP-registered clinician. Book your initial assessment today."
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background decorations */}
        <div className="blob-decoration w-96 h-96 -top-48 -right-48" />
        <div className="blob-decoration w-64 h-64 top-1/2 -left-32" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8 pb-6 md:pt-16 md:pb-10">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Content */}
            <div className="space-y-5 md:space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-[#E8F3E8] text-[#2D4A3E] px-4 py-2 rounded-full text-sm font-medium">
                <span className="w-2 h-2 bg-[#E09F7D] rounded-full"></span>
                Now accepting new patients
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-[#2D4A3E] leading-tight font-['Fraunces']">
                Helping children reach their full potential
              </h1>
              
              <p className="text-lg text-[#6B7280] max-w-lg leading-relaxed">
                Supporting babies, children and young people with specialist paediatric physiotherapy in a fun, engaging and family-centred environment.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <span className="inline-flex w-[268px] max-w-full h-12" data-testid="hero-book-slot">
                  {pageReady && <Link to="/booking" className="btn-primary w-full h-full flex items-center justify-center gap-2" data-testid="hero-book-btn" title={c.hero_cta_text || "Book Appointment"}>
                    <span className="truncate">{c.hero_cta_text || "Book Appointment"}</span>
                    <ArrowRight className="w-4 h-4 shrink-0" />
                  </Link>}
                </span>
                <Link to="/services">
                  <button className="btn-outline" data-testid="hero-services-btn">
                    Our Services
                  </button>
                </Link>
              </div>

              {/* Trust Badges — Professional credibility */}
              <div className="flex flex-wrap gap-3" data-testid="trust-strip">
                {[
                  { label: "HCPC/APCP/CSP Registered", icon: Shield },
                  { label: "10+ Years Experience", icon: Check },
                  { label: "Specialist Paediatric Physiotherapy", icon: Baby },
                ].map(({ label, icon: Icon }) => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-2 bg-[#E8F3E8] text-[#2D4A3E] rounded-full px-4 py-2 text-sm font-medium shadow-sm"
                  >
                    <Icon className="w-4 h-4 text-[#2D7A54]" />
                    {label}
                  </span>
                ))}</div>

              {/* Google Reviews Badge — Social proof */}
              <a
                href="https://g.page/r/CX0ymNZQf3NEEAE"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-4 group cursor-pointer hover:opacity-90 transition-opacity bg-white/80 backdrop-blur-sm border border-[#E5E7EB] rounded-xl px-5 py-3 shadow-sm"
                data-testid="google-reviews-badge"
              >
                <div className="flex flex-col items-start">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-[#2D4A3E]">5.0</span>
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} className="w-3.5 h-3.5 text-[#FBBF24] fill-[#FBBF24]" />
                      ))}
                    </div>
                    <span className="text-sm text-[#6B7280]">Rated by families</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-[#6B7280] group-hover:text-[#2D4A3E] transition-colors">
                      Read our Google reviews
                    </span>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    <ExternalLink className="w-3.5 h-3.5 text-[#9CA3AF]" />
                  </div>
                </div>
              </a>
            </div>
            
            {/* Hero Image */}
            <div className="relative animate-fade-in-up animation-delay-200">
              <div className="relative rounded-3xl overflow-hidden hero-image-mask img-bg">
                <Picture
                  src="/hero-bowls-therapy.webp"
                  alt="Child physio session in Sandy — paediatric physiotherapist with a young child during a colourful bowls activity"
                  width={1200}
                  height={800}
                  priority
                  objectPosition="50% 38%"
                  className="w-full h-[420px] md:h-[500px] lg:h-[540px] object-cover"
                />
                <div className="absolute inset-0 hero-gradient" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section — premium, cohesive "gallery" treatment */}
      <section
        className="py-20 md:py-28 relative overflow-hidden"
        style={{
          background:
            "linear-gradient(180deg, #FAF6EE 0%, #F5EFE2 55%, #FAF6EE 100%)",
        }}
        data-testid="services-section"
      >
        {/* Very subtle blurred warm orb for depth (non-intrusive) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 -right-40 w-[520px] h-[520px] rounded-full opacity-40 blur-3xl"
          style={{ background: "radial-gradient(closest-side, #E8D9C4, transparent 70%)" }}
        />

        <div className="max-w-6xl mx-auto px-6 md:px-12 relative">
          <div className="text-center mb-16 md:mb-20">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-[#1F3D32] font-['Fraunces'] mb-5 tracking-tight">
              {c.services_title} {c.services_title_highlight}
            </h2>
            <p className="text-[#6B7280] text-base md:text-lg leading-[1.85] max-w-xl mx-auto">
              {c.services_subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12 lg:gap-14">
            {services.map((service, index) => {
              const IconComponent = categoryIconMap[service.category] || Users;
              return (
                <Link
                  key={service.id || service._id}
                  to={`/booking?service=${service.id || service._id}`}
                  className="service-tile group animate-fade-in-up h-full flex flex-col"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`service-card-${service.id || service._id}`}
                >
                  {/* Image — cohesive aspect + warm overlay + hover zoom */}
                  <div className="relative rounded-2xl overflow-hidden aspect-[4/3] service-tile-image mb-7">
                    <PositionedImage
                      src={service.image_url || service.image}
                      alt={service.name || service.title}
                      className="w-full h-full object-cover service-tile-img"
                    />
                    {/* Unifying warm overlay gradient (very light) */}
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(250,246,238,0) 55%, rgba(31,61,50,0.18) 100%)",
                      }}
                    />
                    <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full text-[13px] font-semibold text-[#1F3D32] tracking-wide shadow-sm">
                      {price(service.price)}
                    </div>
                  </div>

                  {/* Content — lighter, breathing */}
                  <div className="flex items-center gap-3 mb-3.5">
                    <div className="w-9 h-9 rounded-xl bg-[#E8F3E8] flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-4.5 h-4.5 text-[#2D7A54]" />
                    </div>
                    <h3 className="text-[22px] md:text-[24px] font-normal text-[#1F3D32] font-['Fraunces'] leading-tight tracking-tight">
                      {service.name || service.title}
                    </h3>
                  </div>

                  <p className="text-[#6B7280] text-[15px] leading-[1.75] max-w-[32ch] mb-6">
                    {service.description}
                  </p>

                  {/* CTA — subtle, premium */}
                  <div className="flex items-center gap-2 text-[#2D7A54] font-medium text-sm tracking-wide mt-auto pt-1 service-tile-cta">
                    <span>Book Now</span>
                    <ArrowRight className="w-4 h-4 service-tile-arrow" />
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-16 md:mt-20">
            <Link to="/services">
              <button className="btn-outline" data-testid="view-all-services-btn">
                View All Services
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Who We Help */}
      <section className="py-8 md:py-12 bg-white" data-testid="who-we-help-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-light text-[#2D4A3E] font-['Fraunces'] mb-4">
                Who we help
              </h2>
              <p className="text-[#6B7280] mb-8 leading-relaxed">
                We support babies, children and young people with a wide range of needs, including support for:
              </p>
              <div className="space-y-4">
                {[
                  "Developmental delay",
                  "Cerebral palsy and neurological conditions",
                  "Balance and coordination difficulties (DCD / dyspraxia)",
                  "Hypermobility and musculoskeletal concerns",
                  "Neuromuscular conditions",
                  "Early years development and baby milestones",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#E8F3E8] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-[#2D4A3E]" />
                    </div>
                    <span className="text-[#374151]">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-[#6B7280] mt-8 leading-relaxed">
                From early developmental concerns to more complex neurological conditions, we are here to support your child every step of the way.
              </p>
              <Link to="/conditions-we-treat">
                <Button
                  className="mt-8 bg-[#2D4A3E] hover:bg-[#1d332a] text-white rounded-full px-8 py-3 text-sm font-medium"
                  data-testid="conditions-we-treat-btn"
                >
                  Conditions We Treat
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative img-bg rounded-3xl">
              <Picture
                src="/balance-disc-therapy.webp"
                alt="Paediatric physio in Sandy — child physiotherapist supporting a toddler on a balance disc during a developmental session"
                width={1200}
                height={1000}
                objectPosition="50% 35%"
                className="rounded-3xl w-full h-[420px] md:h-[540px] lg:h-[580px] object-cover shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Families Choose Growing Together */}
      <section className="py-8 md:py-12 bg-[#E8F3E8]/30" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-light text-[#2D4A3E] font-['Fraunces']">
              Why families choose Growing Together
            </h2>
          </div>

          {/* Standard cards — 3x2 grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Shield,
                title: "Specialist paediatric expertise",
                description: "Over 10 years of experience supporting babies, children and young people across NHS and private settings.",
              },
              {
                icon: Sparkles,
                title: "Fun, personalised therapy",
                description: "Every session is tailored to your child's interests, strengths and goals.",
              },
              {
                icon: Heart,
                title: "Family-centred approach",
                description: "Therapy works best when it fits naturally into everyday life.",
              },
              {
                icon: Users,
                title: "A calm, welcoming studio",
                description: "Specialist therapy in a friendly, relaxed environment without the clinical feel of a hospital.",
              },
              {
                icon: Zap,
                title: "Blocks of intensive therapy",
                description: "Focused blocks of therapy designed to accelerate progress through consistent, targeted sessions tailored to your child.",
              },
              {
                icon: CalendarClock,
                title: "After school & weekend sessions",
                description: "Flexible appointment times including evenings, weekends and school visits to fit around your family's routine.",
              },
            ].map(({ icon: Icon, title, description }) => (
              <div
                key={title}
                className="bg-white rounded-2xl p-8 border border-[#F2F0EB] hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-xl bg-[#E09F7D]/10 flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-[#E09F7D]" />
                </div>
                <h3 className="font-semibold text-[#2D4A3E] text-lg mb-2">{title}</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">{description}</p>
              </div>
            ))}
          </div>

          {/* Feature card — Specialist equipment */}
          <div
            className="mt-6 rounded-2xl p-8 md:p-10 max-w-3xl mx-auto hover:shadow-md transition-shadow"
            style={{
              background: "linear-gradient(135deg, #EDF5F0 0%, #F5F0EB 100%)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
              border: "1px solid rgba(0,0,0,0.04)",
            }}
          >
            <div className="flex items-center gap-4 mb-5">
              <div className="w-12 h-12 rounded-xl bg-[#2D4A3E] flex items-center justify-center flex-shrink-0">
                <Dumbbell className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-[#2D4A3E] text-lg">Specialist equipment</h3>
            </div>
            <p className="text-[#6B7280] text-sm leading-relaxed mb-5">
              Access to specialist equipment designed to support your child's progress, including:
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { icon: Star, label: "Whole Body Vibration Therapy" },
                { icon: ArrowRight, label: "Treadmill Gait Training" },
                { icon: Shield, label: "Spider Cage Therapy" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-3 bg-white/70 rounded-xl p-4">
                  <div className="w-8 h-8 rounded-lg bg-[#E09F7D]/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-[#E09F7D]" />
                  </div>
                  <span className="text-sm text-[#2D4A3E] font-medium leading-tight">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-8 md:py-12 bg-white" data-testid="how-it-works-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-light text-[#2D4A3E] font-['Fraunces']">
              How it works
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 md:gap-8">
            {[
              {
                step: "1",
                title: "Book an initial assessment",
                description: "We take time to understand your child's needs, strengths and goals.",
              },
              {
                step: "2",
                title: "Receive a personalised plan",
                description: "We recommend the most appropriate treatment approach for your child.",
              },
              {
                step: "3",
                title: "Ongoing support and progress",
                description: "Regular sessions and guidance to help your child build strength, confidence and independence.",
              },
            ].map(({ step, title, description }) => (
              <div key={step} className="text-center">
                <div className="w-14 h-14 rounded-full bg-[#2D4A3E] text-white flex items-center justify-center mx-auto mb-5 text-xl font-semibold font-['Fraunces']">
                  {step}
                </div>
                <h3 className="font-semibold text-[#2D4A3E] text-lg mb-2">{title}</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Your Physiotherapist */}
      <section className="py-12 md:py-20 bg-[#FDFBF7]" data-testid="meet-physio-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-center">
            <div className="relative lg:col-span-2">
              {/* Soft accent glow for premium feel */}
              <div
                className="absolute inset-0 rounded-3xl -m-2"
                style={{ background: "radial-gradient(closest-side, rgba(45,122,84,0.10), transparent 70%)" }}
                aria-hidden="true"
              />
              <Picture
                src="/judy-portrait.jpg"
                alt="Judy Marton, specialist paediatric physiotherapist serving Sandy, Bedford and Hitchin — child physio for babies, children and young people up to age 25"
                width={800}
                height={1000}
                objectPosition="50% 22%"
                className="relative rounded-3xl w-full h-[380px] sm:h-[460px] md:h-[560px] lg:h-[620px] object-cover"
                style={{ boxShadow: "0 24px 48px rgba(31,61,50,0.14)" }}
              />
            </div>
            <div className="space-y-6 lg:col-span-3">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-[#2D4A3E] font-['Fraunces'] tracking-tight">
                Meet your physiotherapist
              </h2>
              <p className="text-[#2D7A54] font-medium text-sm tracking-wide uppercase -mt-2">
                Judy Marton · Paediatric Physiotherapist
              </p>
              <p className="text-[#6B7280] leading-relaxed text-base md:text-lg">
                Judy is a highly specialist paediatric physiotherapist with over 10 years' experience across NHS and private settings. She provides fun, engaging and evidence-based therapy tailored to each child.
              </p>
              <Link to="/about">
                <button className="btn-outline inline-flex items-center gap-2 mt-2" data-testid="meet-physio-btn">
                  Learn More
                  <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Registrations & Accreditations */}
      {/* Testimonials — Real Google Reviews */}
      <section id="testimonials-section" className="py-8 md:py-12 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-light text-[#2D4A3E] font-['Fraunces'] mb-3">
              What families say
            </h2>
            <p className="text-[#6B7280] text-sm flex items-center justify-center gap-2">
              Independent reviews from
              <svg className="w-4 h-4 inline" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Google
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                content: "Judy is professional, patient and incredibly kind. She makes sessions comfortable and explains everything clearly. We always feel supported.",
                name: "Team Hamster",
                role: "Parent",
              },
              {
                content: "Judy creates a calm, friendly and non-clinical environment where children feel comfortable and engaged. Her sessions are fun and tailored, making a real difference.",
                name: "Jules",
                role: "Parent",
              },
              {
                content: "Judy is an incredible professional, knowledgeable, empathetic and amazing with children. She truly understands families and adapts every session perfectly.",
                name: "Malgorzata K.",
                role: "Parent",
              },
              {
                content: "Judy is attentive, kind and highly skilled. Her sessions are engaging and effective, and we've seen real progress thanks to her support.",
                name: "Cassie J.",
                role: "Parent",
              },
            ].map((review, index) => (
              <div
                key={index}
                className="bg-[#FDFBF7] rounded-2xl p-6 border border-[#F2F0EB] shadow-sm hover:shadow-md transition-shadow"
                data-testid={`testimonial-card-${index}`}
              >
                <div className="flex items-center gap-0.5 mb-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-4 h-4 text-[#FBBF24] fill-[#FBBF24]" />
                  ))}
                </div>
                <p className="text-[#374151] text-sm leading-relaxed mb-5">
                  &ldquo;{review.content}&rdquo;
                </p>
                <div className="pt-4 border-t border-[#F2F0EB]">
                  <p className="font-medium text-[#2D4A3E] text-sm">{review.name}</p>
                  <p className="text-xs text-[#9CA3AF]">{review.role}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <a
              href="https://g.page/r/CX0ymNZQf3NEEAE/review"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white border border-[#E5E7EB] hover:border-[#2D4A3E] text-[#2D4A3E] px-6 py-3 rounded-full font-medium transition-colors shadow-sm"
              data-testid="google-reviews-cta"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Read all Google reviews
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Professional Registrations & Accreditations */}
      <section className="py-8 md:py-10 bg-white" data-testid="accreditations-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-6">
            <h2 className="text-3xl md:text-4xl font-light text-[#2D4A3E] font-['Fraunces'] mb-3">
              Professional Registrations and Accreditations
            </h2>
            <p className="text-[#6B7280] text-sm md:text-base max-w-xl mx-auto">
              Registered with recognised professional bodies to ensure safe, high-quality, evidence-based care.
            </p>
          </div>

          <div className="flex items-center justify-center gap-8 md:gap-20 lg:gap-28 flex-wrap">
            <div className="flex flex-col items-center gap-2" data-testid="accreditation-hcpc">
              <Picture src="/hcpc-logo.png" alt="HCPC registered paediatric physiotherapist — Health and Care Professions Council" width={300} height={200} className="h-16 md:h-28 w-auto object-contain" />
              <span className="text-xs text-[#9CA3AF] font-medium tracking-wide">HCPC</span>
            </div>
            <div className="flex flex-col items-center gap-2" data-testid="accreditation-csp">
              <img src="/csp-crest.webp" alt="Chartered Society of Physiotherapy member — child physiotherapy" width="300" height="200" className="h-16 md:h-28 w-auto object-contain" loading="lazy" decoding="async" />
              <span className="text-xs text-[#9CA3AF] font-medium tracking-wide">CSP</span>
            </div>
            <div className="flex flex-col items-center gap-2" data-testid="accreditation-apcp">
              <Picture src="/apcp-logo.png" alt="Association of Paediatric Chartered Physiotherapists member — paediatric physiotherapy in Sandy, Bedford and Hitchin" width={300} height={200} className="h-16 md:h-28 w-auto object-contain" />
              <span className="text-xs text-[#9CA3AF] font-medium tracking-wide">APCP</span>
            </div>
          </div>

          <p className="text-center text-xs text-[#9CA3AF] mt-8">
            Fully insured and GDPR compliant
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-8 md:py-12 bg-[#2D4A3E]" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-white font-['Fraunces'] mb-6">
            Here to support your child's progress.
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Book your initial assessment today and take the first step towards building strength, confidence and independence.
          </p>
          <Link to="/booking">
            <button className="bg-[#E09F7D] text-white rounded-full px-10 py-4 text-lg font-medium hover:-translate-y-1 transition-all shadow-lg hover:shadow-xl" data-testid="cta-book-btn">
              Book Initial Assessment
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
